import { useEffect, useState } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CallablePromise<T> = (...args: any) => Promise<T>;
type ExtractInnerPromise<T> = T extends Promise<infer D> ? D : never;

export function useCallablePromise<
  T,
  FUNCTION extends CallablePromise<T>,
  PARAMETER extends Parameters<FUNCTION>
>(params: {
  promiseFn: FUNCTION;
  args: PARAMETER;
  deps: (string | number)[];
  shouldExecute: boolean;
  onSuccessCallback?: (resp: T) => void;
  resetOnChange?: boolean;
}) {
  const {
    promiseFn,
    args,
    deps,
    shouldExecute,
    onSuccessCallback,
    resetOnChange,
  } = params;
  const [dataState, setDataState] = useState<{
    data?: ExtractInnerPromise<ReturnType<FUNCTION>>;
    isLoading: boolean;
  }>({
    data: undefined,
    isLoading: false,
  });
  useEffect(() => {
    if (resetOnChange) {
      setDataState({ isLoading: false, data: undefined });
    }
    if (shouldExecute) {
      setDataState(old => ({ ...old, isLoading: true }));
      promiseFn(...args).then(resp => {
        setDataState(old => ({
          ...old,
          data: resp as ExtractInnerPromise<ReturnType<FUNCTION>>,
          isLoading: false,
        }));
        onSuccessCallback?.(resp);
      });
    }
    // this function intentionally ignores `args` as a dep in favor of the explicit
    // `deps` array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldExecute, resetOnChange, onSuccessCallback, promiseFn, ...deps]);

  return dataState;
}
